import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  authApi,
  publicConsentApi,
  publicInviteApi,
  RequestError,
  type PublicConsentDocument,
} from '../api/client';
import {
  isDocumentNotOpened,
  isOpenApiMissing,
  versionForGrant,
  type OpenedDocument,
} from '../utils/consentOpen';

/**
 * 招待受諾（FRONTEND_onboarding_flow_spec §3 / §4 / §7）。
 *
 * 2026-04-17〜: BE 提供の未認証エンドポイント
 * `GET /v1/public/consent-documents?tenant_code=xxx` から文書を取得し、
 * 各文書 id/version を accept-invite の tos_agreements に含めて送信する
 */
export default function AcceptInvite() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialToken = useMemo(() => (params.get('invite_token') ?? '').trim(), [params]);
  const initialTenantCode = useMemo(() => (params.get('tenant_code') ?? '').trim(), [params]);

  const [token, setToken] = useState(initialToken);
  /**
   * 招待メールのリンクから開いた場合、トークンとテナントコードは URL から自動入力される
   * （BE PR #7 / 2026-08-25〜）。43 文字のトークンを誤って編集・欠損させる事故を防ぐため、
   * 自動入力されているときは読み取り専用にする（課題 #55）。
   * リンクが使えず手入力したい場合に備え、明示的に解除できるようにしておく。
   */
  const [tokenUnlocked, setTokenUnlocked] = useState(false);
  /** 送信エラーが招待トークン起因か（復帰手段の案内を出すかの判定に使う・課題 #55） */
  const [tokenError, setTokenError] = useState(false);
  const [tenantCode, setTenantCode] = useState(initialTenantCode);
  /** 招待メールのリンク経由で開かれたか（トークンが URL から入っているか） */
  const autofilledFromLink = initialToken !== '';
  const tokenLocked = autofilledFromLink && !tokenUnlocked;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docs, setDocs] = useState<PublicConsentDocument[]>([]);
  /** 同意文書を読み込んだテナントコード（読み込む前や、打ち替えた直後を「文書が 0 件」と取り違えないため） */
  const [docsLoadedFor, setDocsLoadedFor] = useState<string | null>(null);
  /**
   * 招待の役割（課題 #99）。組織に同意文書がまだ無いとき、管理者・産業保健職だけは同意なしでアカウントを作れる。
   * 役割を返す API が無い古い BE や、読めなかったときは null（今までどおり、同意文書への同意が要る）
   */
  const [inviteRole, setInviteRole] = useState<string | null>(null);
  const [agreements, setAgreements] = useState<Record<string, boolean>>({});
  /**
   * 本文を開いた文書（課題 #77 / BE PR #130）。本文を開くまで同意のチェックは付けられない。
   * 開いた記録は招待に結び付くので、招待のトークンが変わったら開き直してもらう。
   */
  const [opened, setOpened] = useState<Record<string, OpenedDocument>>({});
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setToken(initialToken);
  }, [initialToken]);

  // 開いた記録は招待（トークン）に結び付くため、トークンが変わったら同意をやり直してもらう
  useEffect(() => {
    setOpened({});
    setExpandedId(null);
    setAgreements((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, false])));
  }, [token]);

  /** 本文を開く（もう一度押すと閉じる）。開いた時刻はサーバーが招待に結び付けて記録する */
  const toggleDocument = async (doc: PublicConsentDocument) => {
    if (expandedId === doc.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(doc.id);
    setOpenError(null);
    if (opened[doc.id]) return;
    const inviteToken = token.trim();
    if (!inviteToken) return;
    setOpeningId(doc.id);
    try {
      const res = await publicConsentApi.openDocument(doc.id, inviteToken);
      setOpened((prev) => ({ ...prev, [doc.id]: { version: res.version, content: res.content ?? '', legacy: false } }));
    } catch (err) {
      if (isOpenApiMissing(err)) {
        // 本文を開く API が無い古い BE。一覧の本文を使い、これまでどおり同意できるようにする
        setOpened((prev) => ({ ...prev, [doc.id]: { version: doc.version, content: doc.content ?? '', legacy: true } }));
      } else {
        setExpandedId(null);
        setOpenError(err instanceof Error ? err.message : t('acceptInvite.error.generic'));
        setTokenError(err instanceof RequestError && /トークン|token/i.test(err.message));
      }
    } finally {
      setOpeningId(null);
    }
  };

  useEffect(() => {
    setTenantCode(initialTenantCode);
  }, [initialTenantCode]);

  // 招待の役割を読む（課題 #99）。トークンを手で打っている途中に 1 文字ずつ問い合わせないよう、少し待ってから読む
  useEffect(() => {
    setInviteRole(null);
    const inviteToken = token.trim();
    if (!inviteToken) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      publicInviteApi
        .get(inviteToken)
        .then((res) => {
          if (!cancelled) setInviteRole(typeof res?.role === 'string' ? res.role : null);
        })
        .catch(() => {
          // 役割を返す API が無い BE（404）や無効な招待など。今までどおりの扱いにし、理由は送信のときに BE が返す
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token]);

  const loadDocs = useCallback(
    (code: string) => {
      setDocsLoadedFor(null);
      if (!code) {
        setDocs([]);
        setAgreements({});
        setDocsError(null);
        return;
      }
      setDocsLoading(true);
      setDocsError(null);
      publicConsentApi
        .getByTenant(code)
        .then((res) => {
          setDocs(res.documents ?? []);
          setDocsLoadedFor(code);
          setAgreements(
            Object.fromEntries((res.documents ?? []).map((d) => [d.id, false]))
          );
        })
        .catch((err: unknown) => {
          if (err instanceof RequestError) {
            if (err.status === 404) setDocsError(t('acceptInvite.error.tenantNotFound'));
            else if (err.status === 429) setDocsError(t('acceptInvite.error.rateLimited'));
            else setDocsError(err.message);
          } else {
            setDocsError(t('acceptInvite.error.manifestLoad'));
          }
          setDocs([]);
          setAgreements({});
        })
        .finally(() => setDocsLoading(false));
    },
    [t]
  );

  // URL からテナントコードが渡されたら即時取得。手動入力ではサブミット時に取得
  useEffect(() => {
    if (initialTenantCode) {
      loadDocs(initialTenantCode);
    }
  }, [initialTenantCode, loadDocs]);

  const handleTenantCodeBlur = () => {
    const code = tenantCode.trim();
    if (code && docs.length === 0 && !docsLoading) {
      loadDocs(code);
    }
  };

  const passwordTooShort = password.length > 0 && password.length < 8;
  const passwordMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  // 全文書に同意が必要（現時点は BE が必須文書のみを返す想定）
  const allAgreed = docs.length > 0 && docs.every((d) => agreements[d.id] === true);
  /**
   * 組織に同意文書がまだ無いとき、管理者・産業保健職の招待は同意なしで作れる（課題 #99）。
   * 最初の管理者は、ログインするまで同意文書を登録できないため。従業員は今までどおり作れない
   * （同意なしで使い始めないため）。
   */
  const staffWithoutDocs =
    (inviteRole === 'admin' || inviteRole === 'healthcare') &&
    !docsLoading &&
    !docsError &&
    docs.length === 0 &&
    docsLoadedFor !== null &&
    docsLoadedFor === tenantCode.trim();

  const canSubmit =
    token.trim().length > 0 &&
    tenantCode.trim().length > 0 &&
    password.length >= 8 &&
    password === confirmPassword &&
    ((docs.length > 0 && allAgreed) || staffWithoutDocs) &&
    !submitting &&
    !docsLoading;

  const handleAgreement = (id: string, value: boolean) => {
    // 本文を開くまで同意できない（課題 #77）
    if (value && !opened[id]) return;
    setAgreements((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setTokenError(false);
    setSubmitting(true);
    try {
      await authApi.acceptInvite({
        invite_token: token.trim(),
        password,
        // 開いた版で同意する（一覧を読んだ後に改訂されていても、読んだ版と同意が食い違わない）
        tos_agreements: docs.map((d) => ({
          document_id: d.id,
          version: versionForGrant(d.version, opened[d.id]),
        })),
      });
      setSuccess(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (isDocumentNotOpened(err)) {
        // 改訂された・開いた記録が見つからない。本文を開き直して同意し直してもらう
        setOpened({});
        setExpandedId(null);
        setAgreements((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, false])));
        setError(t('acceptInvite.docs.notOpened'));
      } else if (err instanceof RequestError) {
        setError(err.message);
        // BE のメッセージ（例:「無効な招待トークンです」）は活かしつつ、
        // 手写しによる欠損が原因のことが多いため復帰手段を併記する
        setTokenError(/トークン|token/i.test(err.message));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('acceptInvite.error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-card accept-invite-card">
          <h1>{t('acceptInvite.successTitle')}</h1>
          <p className="accept-invite-success" role="status">
            {t('acceptInvite.successBody')}
          </p>
          <button
            type="button"
            className="accept-invite-submit"
            onClick={() => navigate('/login', { replace: true })}
          >
            {t('acceptInvite.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card accept-invite-card">
        <h1>{t('acceptInvite.title')}</h1>
        <p className="login-desc">
          {autofilledFromLink ? t('acceptInvite.introFromLink') : t('acceptInvite.intro')}
        </p>
        <p className="accept-invite-hint">{t('acceptInvite.manualFallbackHint')}</p>
        <form onSubmit={handleSubmit} className="login-form accept-invite-form">
          <label>
            {t('acceptInvite.tenantCodeLabel')}
            <input
              type="text"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              onBlur={handleTenantCodeBlur}
              required
              autoComplete="organization"
              spellCheck={false}
              placeholder={t('acceptInvite.tenantCodePlaceholder')}
            />
          </label>
          <p className="accept-invite-hint">{t('acceptInvite.tenantCodeHint')}</p>

          <label>
            {t('acceptInvite.tokenLabel')}
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              readOnly={tokenLocked}
              autoComplete="off"
              spellCheck={false}
              placeholder={t('acceptInvite.tokenPlaceholder')}
            />
          </label>
          {tokenLocked ? (
            <p className="accept-invite-hint">
              {t('acceptInvite.tokenFilledFromLink')}{' '}
              <button
                type="button"
                className="accept-invite-unlock"
                onClick={() => setTokenUnlocked(true)}
              >
                {t('acceptInvite.tokenEditManually')}
              </button>
            </p>
          ) : (
            <p className="accept-invite-hint">{t('acceptInvite.tokenHint')}</p>
          )}

          <label>
            {t('acceptInvite.passwordLabel')}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </label>
          <p className="accept-invite-hint">{t('acceptInvite.passwordHint')}</p>
          {passwordTooShort && (
            <p className="login-error" role="alert">
              {t('acceptInvite.error.passwordTooShort')}
            </p>
          )}

          <label>
            {t('acceptInvite.confirmPasswordLabel')}
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </label>
          {passwordMismatch && (
            <p className="login-error" role="alert">
              {t('acceptInvite.error.passwordMismatch')}
            </p>
          )}

          <fieldset className="accept-invite-tos">
            <legend>{t('acceptInvite.tosLegend')}</legend>
            {!tenantCode.trim() && (
              <p className="accept-invite-hint">{t('acceptInvite.docs.waitingTenant')}</p>
            )}
            {docsLoading && (
              <p className="accept-invite-hint">{t('common.loading')}</p>
            )}
            {docsError && (
              <p className="login-error" role="alert">
                {docsError}
              </p>
            )}
            {!docsLoading && !docsError && tenantCode.trim() && docs.length === 0 && (
              <p className="accept-invite-hint">
                {t(staffWithoutDocs ? 'acceptInvite.docs.emptyStaff' : 'acceptInvite.docs.empty')}
              </p>
            )}
            {docs.length > 0 && (
              <p className="accept-invite-hint">
                {token.trim() ? t('acceptInvite.docs.openFirst') : t('acceptInvite.docs.tokenFirst')}
              </p>
            )}
            {docs.map((doc) => (
              <div key={doc.id} className="accept-invite-doc" data-testid="invite-doc">
                <label className="accept-invite-check">
                  <input
                    type="checkbox"
                    checked={agreements[doc.id] === true}
                    disabled={!opened[doc.id]}
                    onChange={(e) => handleAgreement(doc.id, e.target.checked)}
                  />
                  <span>
                    {t('acceptInvite.docs.agreeLine', {
                      title: doc.title,
                      version: doc.version,
                    })}
                  </span>
                </label>
                <button
                  type="button"
                  className="accept-invite-unlock"
                  onClick={() => toggleDocument(doc)}
                  disabled={!token.trim() || openingId === doc.id}
                  aria-expanded={expandedId === doc.id}
                >
                  {openingId === doc.id
                    ? t('acceptInvite.docs.opening')
                    : expandedId === doc.id
                    ? t('acceptInvite.docs.hideContent')
                    : t('acceptInvite.docs.showContent')}
                </button>
                {expandedId === doc.id && opened[doc.id] && (
                  <div className="accept-invite-doc-content" data-testid="invite-doc-body">
                    {opened[doc.id].content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                        {opened[doc.id].content}
                      </ReactMarkdown>
                    ) : (
                      <p className="accept-invite-hint">{t('acceptInvite.docs.contentUnavailable')}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
            {openError && (
              <p className="login-error" role="alert">
                {openError}
              </p>
            )}
            <p className="accept-invite-hint">{t('acceptInvite.modeHint')}</p>
          </fieldset>

          {error && (
            <>
              <p className="login-error" role="alert">
                {error}
              </p>
              {tokenError && (
                <p className="accept-invite-hint">
                  {t('acceptInvite.error.invalidToken')}
                </p>
              )}
            </>
          )}

          <button type="submit" className="accept-invite-submit" disabled={!canSubmit}>
            {submitting ? t('common.loading') : t('acceptInvite.submit')}
          </button>
        </form>
        <p className="accept-invite-back">
          <Link to="/login">{t('acceptInvite.backToLogin')}</Link>
        </p>
      </div>
    </div>
  );
}
